import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import {
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS,
  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_PRIZE_POOL,
  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_PRIZE_POOL_CONTENDER,
  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS,
  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS_CONTENDER,
} from '@sorare/core/src/constants/__generated__/routes';
import { Warning } from '@sorare/core/src/contexts/intl/Warning';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { getSafePreviousLocation } from '@sorare/core/src/lib/navigation/getSafePreviousLocation';

import { DivisionSelector } from 'components/leaderboard/DivisionSelector';
import { messages } from 'components/leaderboard/PrizePoolWidget/DefaultPrizePool';
import { WithFixtureHeaderLayout } from 'layouts/WithFixtureHeaderLayout';

import type {
  GenericLeaderboardPrizePoolLayout_so5Leaderboard,
  GenericLeaderboardPrizePoolLayout_so5LeaderboardContender,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 3 })`
  margin-top: var(--double-unit);
  margin-bottom: var(--double-unit);
`;
const Content = styled.div`
  background-color: var(--c-black);
  border-radius: var(--double-unit);
`;

type Props = {
  so5Leaderboard: GenericLeaderboardPrizePoolLayout_so5Leaderboard;
  so5LeaderboardContender?: GenericLeaderboardPrizePoolLayout_so5LeaderboardContender;
  children: ReactNode;
};

export const GenericLeaderboardPrizePoolLayout: FC<
  PropsWithChildren<Props>
> = ({ so5Leaderboard, so5LeaderboardContender, children }: Props) => {
  const { generateSportContextPath, sport } = useSportContext();
  const navigate = useNavigate();
  const { so5Fixture, rewardsConfig, so5LeagueTrack } = so5Leaderboard;

  const hasDivisionLeaderboards = so5LeagueTrack.so5Leaderboards.length > 1;
  const isCumulativeLeaderboard =
    !!so5Leaderboard.universalSo5UserGroups?.length;

  const safePreviousLocation = getSafePreviousLocation(
    generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS, {
      params: {
        eventType: so5Fixture.type.toLowerCase(),
        fixture: so5Fixture.slug,
      },
    })
  );

  const hasNoRewards =
    !rewardsConfig.ranking?.length &&
    !rewardsConfig.conditional?.length &&
    !rewardsConfig.targetable?.length;

  const contenderInLeaderboard =
    so5Leaderboard.slug === so5LeaderboardContender?.so5Leaderboard.slug
      ? so5LeaderboardContender
      : null;

  return (
    <WithFixtureHeaderLayout
      so5Fixture={so5Leaderboard.so5Fixture}
      title={
        <FormattedMessage
          {...fantasy.nameLeaderboard}
          values={{
            displayName:
              sport === Sport.FOOTBALL
                ? so5Leaderboard.so5LeaderboardGroup.displayName
                : so5Leaderboard.displayName,
          }}
        />
      }
      iconUrl={
        sport === Sport.FOOTBALL
          ? so5Leaderboard.so5LeaderboardGroup.iconUrl
          : so5Leaderboard.iconUrl
      }
      backTo={safePreviousLocation}
      action={
        !!hasDivisionLeaderboards && (
          <DivisionSelector
            selectedSo5Leaderboard={so5Leaderboard}
            so5Leaderboards={so5LeagueTrack.so5Leaderboards}
            onChange={so5LeaderboardSlug => {
              navigate(
                generateSportContextPath(
                  so5LeaderboardContender
                    ? ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_PRIZE_POOL_CONTENDER
                    : ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_PRIZE_POOL,
                  {
                    params: {
                      eventType: so5Fixture.type.toLowerCase(),
                      leaderboard: so5LeaderboardSlug,
                      contender: so5LeaderboardContender?.slug ?? '',
                    },
                  }
                ),
                { replace: true }
              );
            }}
          />
        )
      }
    >
      <Root gap={3}>
        <Link
          to={
            contenderInLeaderboard
              ? generateSportContextPath(
                  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS_CONTENDER,
                  {
                    params: {
                      eventType: so5Fixture.type.toLowerCase(),
                      leaderboard: so5Leaderboard.slug,
                      contender: contenderInLeaderboard?.slug,
                    },
                  }
                )
              : generateSportContextPath(
                  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS,
                  {
                    params: {
                      eventType: so5Fixture.type.toLowerCase(),
                      leaderboard: so5Leaderboard.slug,
                    },
                  }
                )
          }
        >
          <BodyM bold className="text-right" color="var(--c-blue-300)">
            <FormattedMessage
              id="Layouts.LeaderboardPrizePoolLayout.seeFullLeaderboard"
              defaultMessage="See full leaderboard"
            />
          </BodyM>
        </Link>
        <Content>
          {hasNoRewards && !isCumulativeLeaderboard ? (
            <Warning variant="yellow">
              <BodyM as="p" bold color="var(--c-yellow-400)">
                <FormattedMessage {...messages.leaderboardHasNoRewards} />
              </BodyM>
            </Warning>
          ) : (
            children
          )}
        </Content>
      </Root>
    </WithFixtureHeaderLayout>
  );
};

GenericLeaderboardPrizePoolLayout.fragments = {
  so5Leaderboard: gql`
    fragment GenericLeaderboardPrizePoolLayout_so5Leaderboard on So5Leaderboard {
      slug
      displayName
      iconUrl
      universalSo5UserGroups {
        slug
      }
      so5Fixture {
        slug
        type
        ...WithFixtureHeaderLayout_so5Fixture
      }
      rewardsConfig {
        ranking {
          id
        }
        conditional {
          id
        }
        targetable {
          id
        }
      }
      so5LeagueTrack {
        slug
        so5Leaderboards {
          slug
          ...DivisionSelector_so5Leaderboard
        }
      }
      so5LeaderboardGroup {
        slug
        displayName
        iconUrl
      }
      ...DivisionSelector_so5Leaderboard
    }
    ${DivisionSelector.fragments.so5Leaderboard}
    ${WithFixtureHeaderLayout.fragments.so5Fixture}
  ` as TypedDocumentNode<GenericLeaderboardPrizePoolLayout_so5Leaderboard>,
  so5LeaderboardContender: gql`
    fragment GenericLeaderboardPrizePoolLayout_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        so5Fixture {
          slug
        }
      }
    }
  ` as TypedDocumentNode<GenericLeaderboardPrizePoolLayout_so5LeaderboardContender>,
};
